import { Navigate, useLocation, useNavigate } from '@remix-run/react';
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import HeroCarouselSection from './HeroCarouselSection';
import { ArrowUpRight, Check } from "lucide-react";
import DesktopFooter from "~/ui-apps/design-system/organisms/DesktopFooter";
import FAQs from "~/ui-apps/design-system/organisms/FAQs";
import LinkButton from "~/ui-apps/design-system/atoms/LinkButton";
import ContactSection from './ContactSection';
import FeaturesSection from './FeaturesSection';
// import AutopilotSection from './AutopilotSection';
import PricingSection from '~/ui-apps/landing-page-app/PricingSection';
import { POST_AUTH_LANDING_PATH } from '~/utilities/postAuthLanding';
import { faUserShield } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import RadarFlywheel from '~/ui-apps/design-system/atoms/RadarFlywheel';

const SCROLL_REVEAL_THRESHOLD_PX = 24;

const GetStartedCta: React.FC<{
  align?: "start" | "center";
  className?: string;
}> = ({ align = "center", className = "" }) => (
  <div
    className={`flex flex-col gap-2.5 ${
      align === "center" ? "items-center text-center" : "items-start text-left"
    } ${className}`}
  >
    <LinkButton
      type="primary"
      size="medium"
      className="w-auto shrink-0 !rounded-lg !px-4 !py-2.5 text-sm shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm sm:!px-5 sm:!py-3"
      icon={<ArrowUpRight className="h-3.5 w-3.5" />}
      iconPosition="right"
      href="/sign-up"
    >
      Start free for 30 days
    </LinkButton>
    <span className="inline-flex items-center gap-1.5 text-[12px] text-yellow-600 sm:text-[13px]">
      <Check
        className="h-3 w-3 shrink-0 text-emerald-600/75 sm:h-3.5 sm:w-3.5"
        strokeWidth={2.5}
        aria-hidden
      />
      No credit card required
    </span>
  </div>
);

const App: React.FC<{ isAuthenticated: boolean, user: any }> = ({ isAuthenticated, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const state = location.state as { scrollToHero?: boolean } | null;
    if (!state?.scrollToHero) return;

    navigate("/", { replace: true, state: {} });
    requestAnimationFrame(() => {
      document.getElementById('hero')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [location.state, navigate]);

  useEffect(() => {
    const updateStickyCta = () => {
      // Sticky CTA is mobile-only; desktop uses in-page CTAs instead.
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      setShowStickyCta(!isDesktop && window.scrollY > SCROLL_REVEAL_THRESHOLD_PX);
    };

    updateStickyCta();
    window.addEventListener('scroll', updateStickyCta, { passive: true });
    window.addEventListener('resize', updateStickyCta, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateStickyCta);
      window.removeEventListener('resize', updateStickyCta);
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={POST_AUTH_LANDING_PATH} />;
  }

  return (
    <div className={`overflow-x-hidden max-w-full ${showStickyCta ? 'pb-[calc(6rem+env(safe-area-inset-bottom,0px))] md:pb-0' : ''}`}>
      {/* Hero Section */}
      <section id="hero" className="relative flex justify-center overflow-hidden px-4 sm:px-6 md:px-8 pt-28 md:pt-40 pb-20 max-w-full">
        {/* Subtle layered rings — arc peeking from top-right */}
        <div
          aria-hidden
          className="pointer-events-none absolute right-[-300px] top-[-320px] z-0 size-[660px] rounded-full select-none lg:hidden"
          style={{
            border: '1px solid color-mix(in srgb, #087a4b 12%, transparent)',
            boxShadow:
              '0 0 0 90px color-mix(in srgb, #087a4b 2%, transparent), 0 0 0 180px color-mix(in srgb, #087a4b 1.25%, transparent)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute right-[-300px] top-[-320px] z-0 hidden size-[660px] rounded-full select-none lg:block"
          style={{
            border: '1px solid color-mix(in srgb, #087a4b 20%, transparent)',
            boxShadow:
              '0 0 0 90px color-mix(in srgb, #087a4b 4%, transparent), 0 0 0 180px color-mix(in srgb, #087a4b 2.5%, transparent)',
          }}
        />

        <motion.div className="relative z-10 mx-auto w-full min-w-0 max-w-[1400px]">
          <div className="grid min-w-0 grid-cols-1 items-center gap-7 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-8 xl:gap-10">
            {/* Column 1: hero copy — hug toward the device on desktop */}
            <div className="w-full min-w-0 self-center text-left lg:ml-auto lg:max-w-[34rem]">
              <motion.div
                className="flex justify-start sm:mb-5"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
              >
                <span
                  className="relative inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 lg:py-2 text-[10.5px] leading-none tracking-wide ring-1 ring-amber-900/10 sm:px-3 sm:text-[11px]"
                  style={{
                    background: "#edd78f",
                    color: "#3a3018",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.65), 0 2px 8px rgba(200, 165, 80, 0.22)",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faUserShield}
                    className="block h-[1em] w-[1em] shrink-0"
                    style={{ color: "#3a3018" }}
                  />
                  <span className="block translate-y-px leading-none">
                    Built for fundamentals-first investors
                  </span>
                </span>
              </motion.div>

              <motion.h1
                className="mb-5 sm:mb-6 mt-5 tracking-tight-relaxed text-5xl sm:text-7xl font-black text-left"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <motion.span
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.3 }}
                >
                  The Research System for Long-Term Investors
                </motion.span>
              </motion.h1>

              <motion.p
                className="mb-6 max-w-[36rem] text-left text-[17px] sm:text-[22px] leading-normal text-gray-500"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.30 }}
              >
                One workspace to intuitively{' '}
                <span className="font-semibold text-gray-900">
                  discover, evaluate, and forecast returns for companies. {' '}
                </span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 1.45 }}
              >
                <GetStartedCta align="start" />
              </motion.div>

              {/* <motion.div
                className="mb-4 flex justify-center lg:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.55 }}
              >
                <RadarFlywheel spinning />
              </motion.div> */}
            </div>

            {/* Column 2: device frame — soft page lift so it sits above the ring wash */}
            <div className="relative mb-8 flex w-full min-w-0 flex-col items-center lg:mb-0 lg:items-end">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-x-6 -inset-y-4 z-0 hidden lg:block xl:-inset-x-10 xl:-inset-y-6"
                // style={{
                //   background:
                //     'radial-gradient(ellipse 78% 70% at 55% 42%, rgba(252,252,249,0.97) 0%, rgba(252,252,249,0.78) 48%, transparent 74%)',
                // }}
              />
              <div className="relative z-10 w-full min-w-0">
                <HeroCarouselSection />
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* <AutopilotSection /> */}
      <FeaturesSection />

      <section className="relative flex w-full justify-center px-4 py-10 sm:px-6 md:px-8 md:py-14">
        <GetStartedCta align="center" />
      </section>

      <PricingSection />
      <FAQs />
      <ContactSection />
      <DesktopFooter />

      <AnimatePresence>
        {showStickyCta && (
          <motion.footer
            className="fixed inset-x-0 bottom-0 z-50 border-t border-black/5 backdrop-blur-md pb-[env(safe-area-inset-bottom,0px)] md:hidden"
            style={{ background: 'rgba(255, 255, 255, 0.80)' }}
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.85 }}
          >
            <div className="mx-auto flex max-w-[1400px] flex-row flex-nowrap items-center justify-center gap-3 px-4 py-3 sm:py-3.5">
              <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] text-yellow-600">
                <Check className="h-3 w-3 shrink-0 text-emerald-600/75" strokeWidth={2.5} aria-hidden />
                No credit card required
              </span>
              <LinkButton
                type="primary"
                size="medium"
                className="w-auto shrink-0 !rounded-lg !px-3.5 !py-2 text-sm shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm sm:!px-4 sm:!py-2.5"
                icon={<ArrowUpRight className="w-3.5 h-3.5" />}
                iconPosition="right"
                href="/sign-up"
              >
                Start free for 30 days
              </LinkButton>
            </div>
          </motion.footer>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;
